import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Star, Upload, Copy } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

const BLANK = { name: "", rating: 5, text: "", image_url: "" };

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [uploading, setUploading] = useState(false);
  const [reviewCount, setReviewCount] = useState("");
  const [savingCount, setSavingCount] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reviews");
      setReviews(r.data || []);
    } catch (e) {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setReviewCount(String(r.data.google_review_count))).catch(() => {});
  }, []);

  const saveReviewCount = async () => {
    const n = parseInt(reviewCount, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Enter a valid number");
      return;
    }
    setSavingCount(true);
    try {
      await api.put("/settings", { google_review_count: n });
      toast.success(`AI listings will now say "+${n} 5-star reviews"`);
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSavingCount(false);
    }
  };

  const startCreate = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const startEdit = (r) => { setEditing(r); setForm({ name: r.name, rating: r.rating, text: r.text, image_url: r.image_url || "" }); setOpen(true); };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.files?.[0]?.url;
      if (url) setForm((f) => ({ ...f, image_url: url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, rating: parseInt(form.rating, 10) || 5, image_url: form.image_url || null };
    try {
      if (editing) {
        await api.put(`/reviews/${editing.id}`, payload);
        toast.success("Review updated");
      } else {
        await api.post("/reviews", payload);
        toast.success("Review added — it's live on the homepage");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save review");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the review from ${r.name}?`)) return;
    try {
      await api.delete(`/reviews/${r.id}`);
      toast.success("Review deleted");
      load();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-3xl text-primary">Customer Reviews</h2>
          <p className="text-sm text-muted-foreground">{reviews.length} review{reviews.length !== 1 ? "s" : ""} showing on your homepage.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              const msg = `Thanks for choosing Pit Stop Motors! If you had a good experience with us, would you mind leaving a quick Google review? It means the world to a small family lot: ${BRAND.googleReviewUrl}`;
              try {
                await navigator.clipboard.writeText(msg);
                toast.success("Review request message copied — paste it into any text or email");
              } catch (e) {
                window.prompt("Copy this review request message:", msg);
              }
            }}
            className="rounded-sm gap-1"
            data-testid="copy-review-request"
          >
            <Copy className="h-3.5 w-3.5" /> Copy Review Request
          </Button>
          <Button onClick={startCreate} className="rounded-sm gap-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="admin-add-review">
            <Plus className="h-4 w-4" /> Add Review
          </Button>
        </div>
      </div>

      <div className="mt-6 border border-soft bg-card p-4 flex items-end gap-3 flex-wrap" data-testid="review-count-card">
        <div>
          <Label htmlFor="review-count" className="text-xs">Google review count (used in AI listing footers)</Label>
          <Input
            id="review-count"
            type="number"
            min="0"
            value={reviewCount}
            onChange={(e) => setReviewCount(e.target.value)}
            className="mt-1 w-32"
            data-testid="review-count-input"
          />
        </div>
        <Button onClick={saveReviewCount} disabled={savingCount} variant="outline" className="rounded-sm" data-testid="review-count-save">
          {savingCount ? "Saving…" : "Save"}
        </Button>
        <p className="text-xs text-muted-foreground pb-2.5">
          Listings will say "Check out our +{reviewCount || "40"} 5-star reviews on Google!" — bump this as reviews come in.
        </p>
      </div>

      <div className="mt-6 space-y-3">
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && reviews.length === 0 && (
          <div className="border border-dashed border-soft p-12 text-center text-muted-foreground" data-testid="reviews-empty">
            <Star className="h-6 w-6 mx-auto opacity-40" />
            <p className="mt-3 font-serif text-xl text-primary">No reviews yet.</p>
            <p className="mt-1 text-sm">Add kind words from happy customers — they'll appear on your homepage.</p>
          </div>
        )}
        {reviews.map((r) => (
          <div key={r.id} className="border border-soft bg-card p-5 flex items-start gap-4" data-testid={`review-row-${r.id}`}>
            {r.image_url && (
              <img src={r.image_url} alt={r.name} className="h-16 w-16 object-cover rounded-sm border border-soft" />
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-primary">{r.name}</span>
                <span className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className={`h-3.5 w-3.5 ${i <= r.rating ? "text-gold fill-gold" : "text-border"}`} />
                  ))}
                </span>
                <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{r.text}</p>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" onClick={() => startEdit(r)} title="Edit" data-testid={`edit-review-${r.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => remove(r)} title="Delete" data-testid={`delete-review-${r.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl text-primary">{editing ? "Edit Review" : "Add a Review"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label htmlFor="rv-name">Customer name *</Label>
              <Input id="rv-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name-input" />
            </div>
            <div>
              <Label>Rating</Label>
              <div className="mt-1 flex gap-1" data-testid="review-rating-picker">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button key={i} type="button" onClick={() => setForm({ ...form, rating: i })} data-testid={`rating-star-${i}`}>
                    <Star className={`h-6 w-6 transition-colors ${i <= form.rating ? "text-gold fill-gold" : "text-border hover:text-gold"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="rv-text">Their words *</Label>
              <Textarea id="rv-text" required rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="review-text-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="rv-img">Photo (optional)</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm gap-1" data-testid="review-upload-btn">
                  <Upload className="h-3 w-3" /> {uploading ? "Uploading…" : "Upload photo"}
                </Button>
                <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="review-file-input" />
              </div>
              <Input id="rv-img" placeholder="https://… or upload above" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="review-image-input" />
              {form.image_url && (
                <img src={form.image_url} alt="Preview" className="mt-2 h-24 w-24 object-cover rounded-sm border border-soft" data-testid="review-image-preview" />
              )}
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="review-save">{editing ? "Save Changes" : "Add Review"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
